import * as React from 'react';
import {
  Box, Button, Chip, Grid, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  ToggleButton, ToggleButtonGroup, Typography, useTheme,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import { IHotspotAnalysis, IHotspotItem } from '../../../shared/services/analytics/hotspots.model';
import { getMixedColorPalette } from '../../../shared/utils/colors-utils';
import { getHotspotComparison } from '../../../shared/utils/hotspot-utils';
import { AnalyticMetric } from './graphs/analytic-metric';
import { GraphBubbleChart } from './graphs/graph-bubble-chart';
import { GraphParetoChart } from './graphs/graph-pareto-chart';
import { GraphLineChart } from './graphs/graph-line-chart';
import { HotspotMetric } from './hotspot-metric-drawer';
// DeltaCell lives in its own file so the Emissions Breakdown page renders deltas identically
// rather than keeping a second copy that drifts.
import { DeltaCell } from './delta-cell';

const fmtTco2e = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (value: number | null | undefined) =>
  value === null || value === undefined || Number.isNaN(value) ? '–' : `${value.toFixed(1)}%`;

// How many ranked items to show before the user asks for the rest. Matches the old server-side
// default, so the page looks the same on arrival and only changes when someone wants more.
export const RANKED_PREVIEW_COUNT = 12;

/**
 * The body of the Emissions Hotspot Analysis page: KPI cards, prioritisation matrix and Pareto
 * chart, ranked table, data quality and reduction context, and the footprint trajectory.
 *
 * Shared by the page and its PDF print page so the two cannot drift. On the page the handlers make
 * it interactive (metric drawers, drill-in, "Show all", the dimension toggle). printMode renders the
 * same figures for paper: nothing is clickable, tooltips and the drill column are dropped, charts
 * draw without animation, and paired panels sit side by side in fixed halves, because Chrome lays a
 * PDF out at paper width and the responsive breakpoints would stack them.
 */
export const HotspotAnalysisBody: React.FC<{
  data: IHotspotAnalysis;
  matrixDimension: string;
  dimensionLabel: (dimension: string) => string;
  showAllRanked: boolean;
  onToggleShowAll?: () => void;
  onSelectMetric?: (metric: HotspotMetric) => void;
  onDrill?: (item?: IHotspotItem) => void;
  onDimensionChange?: (dimension: string) => void;
  printMode?: boolean;
}> = ({
  data,
  matrixDimension,
  dimensionLabel,
  showAllRanked,
  onToggleShowAll,
  onSelectMetric,
  onDrill,
  onDimensionChange,
  printMode = false,
}) => {
  const theme = useTheme();
  const screenLabelsContext = React.useContext(ScreenLabelsContext);
  const colors = getMixedColorPalette(theme.palette.primary.main);
  const { prevTotal, prevLabel } = getHotspotComparison(data);

  const activeDimension = data.dimensions?.[matrixDimension];
  const activeMatrix = data.matrices?.[matrixDimension];

  // Nothing on paper responds to a click.
  const selectMetric = printMode ? undefined : onSelectMetric;
  const drill = printMode ? undefined : onDrill;
  const tooltip = (text: string) => (printMode ? undefined : text);

  const quarterColumn = printMode ? { xs: 3 } : { xl: 3, lg: 3, md: 6, sm: 12, xs: 12 };
  const halfColumn = printMode ? { xs: 6 } : { xl: 6, lg: 6, md: 12, sm: 12, xs: 12 };
  // On paper a row of panels moves to the next page whole rather than splitting across two.
  const rowSx = printMode ? { breakInside: 'avoid' as const } : {};
  // On paper the charts are shorter so the summary page (KPIs, matrix, Pareto and trajectory) fits
  // on one A4 landscape page, and the ranked table starts the next page with the panels after it.
  const matrixChartHeight = printMode ? 240 : undefined;
  const trajectoryChartHeight = printMode ? 140 : undefined;
  // On paper the panels and tables sit a little tighter, so the ranked table and the two panels after
  // it share a page.
  const panelPadding = printMode ? 3 : 4;
  const tableSx = printMode ? { '& .MuiTableCell-root': { py: 0.5 } } : undefined;
  const hasTrajectory = data.reduction.trajectory.filter((t) => t.value !== null).length > 1;

  const trajectory = hasTrajectory && (
    <Grid container spacing={2} sx={{ mt: 0.5, ...rowSx }}>
      <Grid item xs={12}>
        <GraphLineChart
          title='Footprint Trajectory'
          tooltip={tooltip('Total emissions by reporting year (net of offsets where available), showing the overall direction of travel.')}
          dataset={data.reduction.trajectory.map((t) => ({ year: t.year, value: t.value ?? 0 }))}
          series={[{ dataKey: 'value', label: 'Total tCO2e' }]}
          dataKey='year'
          colors={colors}
          skipAnimation={printMode || undefined}
          chartHeight={trajectoryChartHeight}
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 1, ...rowSx }}>
        <Grid item {...quarterColumn} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AnalyticMetric
            title='Total Footprint'
            tooltip={tooltip('Total greenhouse gas emissions for the selected report period.')}
            value={data.totals.total}
            units='tCO2e'
            comparison={prevTotal !== undefined ? data.totals.total - prevTotal : undefined}
            comparisonLabel={prevLabel}
            onClick={selectMetric ? () => selectMetric('total') : undefined}
          />
        </Grid>
        <Grid item {...quarterColumn} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AnalyticMetric
            title='Top Hotspot Share'
            tooltip={tooltip(`The single largest contributor's share of categorised ${dimensionLabel(matrixDimension).toLowerCase()} emissions.`)}
            value={activeDimension?.topShare ?? 0}
            units='%'
            onClick={selectMetric ? () => selectMetric('topShare') : undefined}
          />
        </Grid>
        <Grid item {...quarterColumn} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AnalyticMetric
            title='Drivers of 80%'
            tooltip={tooltip(`Number of ${dimensionLabel(matrixDimension).toLowerCase()} contributors that together make up 80% of the footprint (Pareto). Fewer means more concentrated.`)}
            value={activeDimension?.paretoCount80 ?? 0}
            units={`of ${activeDimension?.itemCount ?? 0}`}
            onClick={selectMetric ? () => selectMetric('drivers') : undefined}
          />
        </Grid>
        <Grid item {...quarterColumn} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Paper
            variant='outlined'
            onClick={selectMetric ? () => selectMetric('biggestMover') : undefined}
            sx={{
              p: 4, height: '100%', minWidth: 0, overflow: 'hidden',
              ...(selectMetric && {
                cursor: 'pointer',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
              }),
            }}
          >
            <Typography variant='subtitle1'><b>Biggest Mover</b></Typography>
            {data.biggestMover ? (
              <>
                <Typography variant='body2' color='text.secondary' noWrap title={data.biggestMover.label}>
                  {data.biggestMover.label}
                </Typography>
                <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mt: 0.5 }}>
                  <ArrowUpwardIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
                  <Typography variant='h6' color='text.primary'>
                    {fmtTco2e.format(data.biggestMover.deltaAbs ?? 0)} tCO2e
                  </Typography>
                </Stack>
              </>
            ) : (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                {data.comparison ? 'No increases vs the comparison period.' : 'No comparison period available.'}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Dimension toggle (the print page names the dimension in its header instead) */}
      {!printMode && onDimensionChange && (
        <Stack direction='row' alignItems='center' flexWrap='wrap' gap={1} sx={{ mt: 2, mb: 1 }}>
          <Typography variant='body2' color='text.secondary'>Analyse hotspots by:</Typography>
          <ToggleButtonGroup
            value={matrixDimension}
            exclusive
            size='small'
            onChange={(_e, value: string | null) => { if (value) onDimensionChange(value); }}
          >
            {(data.matrixDimensions || []).map((dimension) => (
              <ToggleButton key={dimension} value={dimension} sx={{ textTransform: 'none', px: 1.5 }}>
                {dimensionLabel(dimension)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      )}

      {/* Matrix + Pareto. On paper the dimension toggle above is gone, so the row takes the same
          gap as the rows below it. */}
      <Grid container spacing={2} sx={printMode ? { mt: 0.5, ...rowSx } : undefined}>
        <Grid item {...halfColumn} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {activeMatrix && (
            <GraphBubbleChart
              title='Hotspot Prioritisation Matrix'
              tooltip={tooltip('Each point is a contributor positioned by its share of the footprint (vertical) against its change vs the comparison period (horizontal). Top-right = large and growing = highest priority. Click a point to drill in.')}
              points={activeMatrix.points}
              impactMedian={activeMatrix.impactMedian}
              hasComparison={activeMatrix.hasComparison}
              chartHeight={matrixChartHeight}
              onSelect={drill
                ? (point) => drill(activeDimension?.items.find((i) => (point.id && i.id === point.id) || i.label === point.label))
                : undefined}
            />
          )}
        </Grid>
        <Grid item {...halfColumn} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <GraphParetoChart
            title={`Concentration of Emissions by ${dimensionLabel(matrixDimension)}`}
            tooltip={tooltip('Bars show each contributor in descending order; the line shows the running cumulative share. Where the line crosses 80% tells you the minimum set worth acting on. Click a bar to drill in.')}
            // Capped deliberately: a Pareto of 300 bars is unreadable, and the chart's job
            // is to show concentration among the leaders. The full list lives in the table.
            items={(activeDimension?.items ?? []).slice(0, RANKED_PREVIEW_COUNT).map((item) => ({ label: item.label, value: item.value, cumulativePct: item.cumulativePct }))}
            onSelect={drill ? (dataIndex) => drill(activeDimension?.items[dataIndex]) : undefined}
            skipAnimation={printMode || undefined}
            chartHeight={matrixChartHeight}
          />
        </Grid>
      </Grid>

      {/* On paper the trajectory joins the other charts on the summary page. */}
      {printMode && trajectory}

      {/* Ranked hotspot table. On paper it starts a new page and is not kept whole: expanded, it
          runs across pages. */}
      <Grid container spacing={2} sx={printMode ? { mt: 0.5, breakBefore: 'page' } : { mt: 0.5 }}>
        <Grid item xs={12}>
          <Paper variant='outlined' sx={{ p: panelPadding }}>
            <Typography variant='subtitle1' sx={{ mb: 2 }}>
              <b>Ranked Hotspots: {dimensionLabel(matrixDimension)}</b>
            </Typography>
            <TableContainer>
              <Table size='small' sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, pl: 0 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{dimensionLabel(matrixDimension)}</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>tCO₂e</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>Share</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Cumulative</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{prevLabel || 'Δ vs prior'}</TableCell>
                    {matrixDimension === 'scope' && <TableCell align='center' sx={{ fontWeight: 600 }}>Reduction action</TableCell>}
                    {drill && <TableCell align='center' sx={{ fontWeight: 600, width: 48 }} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(showAllRanked ? (activeDimension?.items ?? []) : (activeDimension?.items ?? []).slice(0, RANKED_PREVIEW_COUNT)).map((item) => (
                    <TableRow key={`${item.rank}-${item.label}`} sx={printMode ? { breakInside: 'avoid' } : undefined}>
                      <TableCell sx={{ pl: 0, color: 'text.secondary' }}>{item.rank}</TableCell>
                      <TableCell>{item.label}</TableCell>
                      <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtTco2e.format(item.value)}</TableCell>
                      <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtPct(item.sharePct)}</TableCell>
                      <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{fmtPct(item.cumulativePct)}</TableCell>
                      <TableCell align='right'>
                        <DeltaCell deltaAbs={item.deltaAbs} deltaPct={item.deltaPct} isNew={item.isNew} />
                      </TableCell>
                      {matrixDimension === 'scope' && (
                        <TableCell align='center'>
                          {item.hasReductionAction === null || item.hasReductionAction === undefined
                            ? <Typography variant='body2' color='text.secondary'>–</Typography>
                            : item.hasReductionAction
                              ? <Chip size='small' label='Covered' color='success' variant='outlined' />
                              : <Chip size='small' label='No action' color='default' variant='outlined' />}
                        </TableCell>
                      )}
                      {drill && (
                        <TableCell align='center' sx={{ pr: 0 }}>
                          <Tooltip title='Compare & drill in'>
                            <span>
                              <IconButton size='small' disabled={!item.id} onClick={() => drill(item)}>
                                <InsightsOutlinedIcon fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!printMode && onToggleShowAll && (activeDimension?.items?.length ?? 0) > RANKED_PREVIEW_COUNT && (
              <Box sx={{ pt: 1 }}>
                <Button variant='text' size='small' onClick={onToggleShowAll}>
                  {showAllRanked
                    ? 'Show fewer'
                    : `Show all ${activeDimension?.itemCount ?? activeDimension?.items.length} ${dimensionLabel(matrixDimension).toLowerCase()} entries`}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Data quality + Reduction context */}
      <Grid container spacing={2} sx={{ mt: 0.5, ...rowSx }}>
        <Grid item {...halfColumn} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Paper variant='outlined' sx={{ p: panelPadding, height: '100%', minWidth: 0, overflow: 'hidden' }}>
            <Typography variant='subtitle1' sx={{ mb: 1 }}><b>Data Quality of Hotspots</b></Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Spend-estimated emissions are less certain, so large spend-based hotspots are both a reduction target and a data-improvement target.
            </Typography>
            <Stack direction='row' useFlexGap flexWrap='wrap' sx={{ mb: 2, gap: 3 }}>
              <Box>
                <Typography variant='h5'>{fmtPct(data.dataQuality.spendBasedPct)}</Typography>
                <Typography variant='caption' color='text.secondary'>of footprint is spend-estimated</Typography>
              </Box>
              <Box>
                <Typography variant='h5'>{data.dataQuality.missingSupplierCount}</Typography>
                <Typography variant='caption' color='text.secondary'>records missing {screenLabelsContext.getLabel('supplier', { plural: false }).toLowerCase()}</Typography>
              </Box>
              <Box>
                <Typography variant='h5'>{data.dataQuality.missingLocationCount}</Typography>
                <Typography variant='caption' color='text.secondary'>records missing {screenLabelsContext.getLabel('location', { plural: false }).toLowerCase()}</Typography>
              </Box>
            </Stack>
            {data.dataQuality.topSpendCategories.length > 0 && (
              <TableContainer>
                <Table size='small' sx={tableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, pl: 0 }}>Top spend-estimated categories</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 600 }}>tCO₂e</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.dataQuality.topSpendCategories.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell sx={{ pl: 0 }}>{row.label}</TableCell>
                        <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtTco2e.format(row.value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        <Grid item {...halfColumn} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Paper variant='outlined' sx={{ p: panelPadding, height: '100%', minWidth: 0, overflow: 'hidden' }}>
            <Typography variant='subtitle1' sx={{ mb: 1 }}><b>Reduction Context</b></Typography>
            <Stack direction='row' useFlexGap flexWrap='wrap' sx={{ mb: 2, gap: 3 }}>
              <Box>
                <Typography variant='h5'>{data.reduction.actions.count}</Typography>
                <Typography variant='caption' color='text.secondary'>reduction actions</Typography>
              </Box>
              <Box>
                <Typography variant='h5'>{fmtTco2e.format(data.reduction.actions.plannedAnnualReductionTco2e)}</Typography>
                <Typography variant='caption' color='text.secondary'>planned annual reduction (tCO2e)</Typography>
              </Box>
              <Box>
                <Typography variant='h5'>{fmtTco2e.format(data.reduction.offsetsTotalTco2e)}</Typography>
                <Typography variant='caption' color='text.secondary'>offsets applied (tCO2e)</Typography>
              </Box>
            </Stack>
            <Stack direction='row' useFlexGap flexWrap='wrap' alignItems='center' sx={{ mb: 2, gap: 1 }}>
              <Typography variant='body2' color='text.secondary'>Action coverage:</Typography>
              {([['1', data.reduction.actions.scopeCoverage.scope1], ['2', data.reduction.actions.scopeCoverage.scope2], ['3', data.reduction.actions.scopeCoverage.scope3]] as Array<[string, boolean]>).map(([scope, covered]) => (
                <Chip key={scope} size='small' label={`Scope ${scope}`} color={covered ? 'success' : 'default'} variant={covered ? 'filled' : 'outlined'} />
              ))}
            </Stack>
            {data.reduction.target.exists ? (
              <Typography variant='body2' color='text.secondary'>
                Target: {data.reduction.target.baseYear || '?'} → {data.reduction.target.targetYear || '?'}
                {data.reduction.target.calculationMethod ? ` (${data.reduction.target.calculationMethod})` : ''}
              </Typography>
            ) : (
              <Typography variant='body2' color='text.secondary'>No reduction target set for this organisation.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Footprint trajectory, full width: a time series reads better wide and
          avoids nesting a responsive chart inside a half-width flex column */}
      {!printMode && trajectory}
    </>
  );
};
